import React from 'react';
import { CreditCard, Clock, Building2, Banknote } from 'lucide-react';
import { motion } from 'framer-motion';

export function PaymentOptions({ paymentMethod, onChange, formatIDR, totalPrice }) {
  return (
    <div className="mt-8 pt-8 border-t border-border" data-testid="payment-options-section">
      <h3 className="text-xl font-heading font-semibold mb-2">Payment Method</h3>
      <p className="text-sm text-foreground-muted mb-6">
        Choose how you want to pay for your booking
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Pay Now - Stripe */}
        <button
          type="button"
          onClick={() => onChange('pay_now')}
          className={`p-6 border-2 transition-all text-left relative overflow-hidden ${
            paymentMethod === 'pay_now'
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50'
          }`}
          data-testid="payment-pay-now"
        >
          {paymentMethod === 'pay_now' && (
            <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs px-3 py-1">
              Selected
            </div>
          )}
          
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg ${paymentMethod === 'pay_now' ? 'bg-primary/20' : 'bg-muted'}`}>
              <CreditCard className={`w-6 h-6 ${paymentMethod === 'pay_now' ? 'text-primary' : 'text-foreground-muted'}`} />
            </div>
            <div className="flex-1">
              <h4 className="font-heading font-semibold text-lg mb-1">Pay now</h4>
              <p className="text-sm text-foreground-muted mb-3">
                Secure payment by credit card via Stripe
              </p>
              <div className="flex items-center gap-2">
                <img 
                  src="https://cdn-icons-png.flaticon.com/512/349/349221.png" 
                  alt="Visa" 
                  className="h-6 w-auto opacity-70"
                />
                <img 
                  src="https://cdn-icons-png.flaticon.com/512/349/349228.png" 
                  alt="Mastercard" 
                  className="h-6 w-auto opacity-70"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center gap-2 text-green-500 text-sm">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Instant confirmation</span>
            </div>
          </div>
        </button>

        {/* Pay Later */}
        <button
          type="button"
          onClick={() => onChange('pay_later')}
          className={`p-6 border-2 transition-all text-left relative overflow-hidden ${
            paymentMethod === 'pay_later'
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50'
          }`}
          data-testid="payment-pay-later"
        >
          {paymentMethod === 'pay_later' && (
            <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs px-3 py-1">
              Selected
            </div>
          )}
          
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-lg ${paymentMethod === 'pay_later' ? 'bg-primary/20' : 'bg-muted'}`}>
              <Clock className={`w-6 h-6 ${paymentMethod === 'pay_later' ? 'text-primary' : 'text-foreground-muted'}`} />
            </div>
            <div className="flex-1">
              <h4 className="font-heading font-semibold text-lg mb-1">Pay later</h4>
              <p className="text-sm text-foreground-muted mb-3">
                Bank transfer or cash at the studio
              </p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-xs text-foreground-muted">
                  <Building2 className="w-4 h-4" />
                  <span>Transfer</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-foreground-muted">
                  <Banknote className="w-4 h-4" />
                  <span>Cash</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center gap-2 text-amber-500 text-sm">
              <Clock className="w-4 h-4" />
              <span>An agent will contact you</span>
            </div>
          </div>
        </button>
      </div>

      {/* Info message based on selection */}
      {paymentMethod === 'pay_later' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 text-sm"
        >
          <p className="text-amber-200">
            <strong>Important:</strong> Your booking will be pending confirmation. 
            A team member will contact you within 24 hours to finalize payment 
            via bank transfer (BCA/Mandiri) or to arrange cash payment at the studio.
          </p>
        </motion.div>
      )}

      {paymentMethod === 'pay_now' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-4 bg-green-500/10 border border-green-500/30 text-sm"
        >
          <p className="text-green-200">
            You will be redirected to the secure Stripe payment page. 
            Your booking will be confirmed immediately after payment.
          </p>
        </motion.div>
      )}
    </div>
  );
}
